import React, { useRef, useCallback, useState, useEffect } from "react";
import { Box, IconButton, Tooltip, useMediaQuery } from "@mui/material";
import {
  SendRounded,
  AttachFileRounded,
  AddRounded,
  CameraAltRounded,
  CloseRounded,
} from "@mui/icons-material";
import useTranslation from "../../hooks/useTranslation";
import { dt } from "../../themes/redesignTokens";
import ActionToast from "../../components/ActionToast";

// ═══════════════════════════════════════════════════════════════
//  MIDAS Design Tokens (local subset)
// ═══════════════════════════════════════════════════════════════


const midas = {
  gold: "#D4A017",
  goldDark: "#A67C00",
  goldGlow: "rgba(212, 160, 23, 0.15)",
  goldGlowRing: "rgba(212, 160, 23, 0.12)",
};

const MAX_CHARS = 5000;
const COUNTER_THRESHOLD = 4500;
const MAX_IMAGE_SIZE_BYTES = 10 * 1024 * 1024; // 10 MB
const MAX_IMAGE_DIMENSION = 2048;
const JPEG_QUALITY = 0.85;
const ACCEPTED_IMAGE_TYPES = [
  "image/jpeg",
  "image/png",
  "image/webp",
  "image/heic",
];
const ACCEPTED_IMAGE_EXTENSIONS = ".jpeg,.jpg,.png,.webp,.heic";

// Capsule variant sizing
const CAPSULE_MAX_HEIGHT = 380; // ~19 lines before internal scroll
const CAPSULE_MIN_HEIGHT_DESKTOP = 21; // 1 line (14px * 1.5 line-height)
const CAPSULE_MIN_HEIGHT_PHONE = 21; // 1 line

// ═══════════════════════════════════════════════════════════════
//  Image resize helper
// ═══════════════════════════════════════════════════════════════

const resizeImageForUpload = (file: File): Promise<File> => {
  return new Promise((resolve) => {
    const isPng = file.type === "image/png";
    const outputMime = isPng ? "image/png" : "image/jpeg";
    const outputExt = isPng ? ".png" : ".jpg";

    const img = new Image();
    const objectUrl = URL.createObjectURL(file);

    img.onload = () => {
      URL.revokeObjectURL(objectUrl);
      const { naturalWidth: w, naturalHeight: h } = img;

      if (w <= MAX_IMAGE_DIMENSION && h <= MAX_IMAGE_DIMENSION) {
        resolve(file);
        return;
      }

      const scale = Math.min(MAX_IMAGE_DIMENSION / w, MAX_IMAGE_DIMENSION / h);
      const newW = Math.round(w * scale);
      const newH = Math.round(h * scale);

      const canvas = document.createElement("canvas");
      canvas.width = newW;
      canvas.height = newH;
      const ctx = canvas.getContext("2d");
      if (!ctx) {
        resolve(file);
        return;
      }
      ctx.drawImage(img, 0, 0, newW, newH);

      canvas.toBlob(
        (blob) => {
          if (!blob) {
            resolve(file);
            return;
          }
          const stem = file.name.replace(/\.[^.]+$/, "");
          const resizedFile = new File([blob], `${stem}${outputExt}`, {
            type: outputMime,
            lastModified: Date.now(),
          });
          resolve(resizedFile);
        },
        outputMime,
        isPng ? undefined : JPEG_QUALITY
      );
    };

    img.onerror = () => {
      URL.revokeObjectURL(objectUrl);
      resolve(file);
    };

    img.src = objectUrl;
  });
};

// ═══════════════════════════════════════════════════════════════
//  ChatInputArea
// ═══════════════════════════════════════════════════════════════

interface ChatInputAreaProps {
  onSend: (message: string, image?: File) => void;
  disabled?: boolean;
  /** When true, the image upload button is disabled with a tooltip. Used in platform mode (no machine selected). */
  imageUploadDisabled?: boolean;
  /** "capsule" = Claude Desktop-style floating capsule (MIDAS full page). Default = compact widget style. */
  variant?: "capsule" | "compact";
}

const ChatInputArea: React.FC<ChatInputAreaProps> = ({
  onSend,
  disabled = false,
  imageUploadDisabled = false,
  variant = "compact",
}) => {
  const { translate } = useTranslation("chatBot");
  const isPhone = useMediaQuery("(max-width:768px)");
  const isTablet = useMediaQuery("(min-width:769px) and (max-width:1024px)");
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [value, setValue] = useState("");
  const [focused, setFocused] = useState(false);
  const [attachedImage, setAttachedImage] = useState<File | null>(null);
  const [imagePreviewUrl, setImagePreviewUrl] = useState<string | null>(null);
  const [snackError, setSnackError] = useState<string | null>(null);

  const isCapsule = variant === "capsule";
  const charCount = value.length;
  const showCounter = charCount >= COUNTER_THRESHOLD;
  const hasText = value.trim().length > 0;
  const canSend = (hasText || attachedImage !== null) && !disabled;

  // Capsule textarea sizing
  const capsuleMinHeight = isPhone ? CAPSULE_MIN_HEIGHT_PHONE : CAPSULE_MIN_HEIGHT_DESKTOP;

  // Auto-resize textarea
  const autoResize = useCallback(() => {
    const el = textareaRef.current;
    if (!el) return;
    if (isCapsule) {
      // Reset to 0 to get accurate scrollHeight (not minHeight, which inflates it)
      el.style.height = "0px";
      const contentHeight = el.scrollHeight;
      const newHeight = Math.max(capsuleMinHeight, Math.min(contentHeight, CAPSULE_MAX_HEIGHT));
      el.style.height = `${newHeight}px`;
      // Only show scrollbar when content exceeds max
      el.style.overflowY = contentHeight > CAPSULE_MAX_HEIGHT ? "auto" : "hidden";
    } else {
      el.style.height = "36px";
      el.style.height = Math.min(el.scrollHeight, 100) + "px";
    }
  }, [isCapsule, capsuleMinHeight]);

  useEffect(() => {
    autoResize();
  }, [value, autoResize]);

  // Generate preview URL for attached image
  useEffect(() => {
    if (!attachedImage) {
      if (imagePreviewUrl) {
        URL.revokeObjectURL(imagePreviewUrl);
      }
      setImagePreviewUrl(null);
      return;
    }
    const url = URL.createObjectURL(attachedImage);
    setImagePreviewUrl(url);
    return () => {
      URL.revokeObjectURL(url);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [attachedImage]);

  const handleSend = useCallback(() => {
    const trimmed = value.trim();
    if (!trimmed && !attachedImage) return;
    if (disabled) return;
    onSend(trimmed, attachedImage || undefined);
    setValue("");
    setAttachedImage(null);
    // Force reset textarea height after React re-renders with empty value
    requestAnimationFrame(() => {
      const el = textareaRef.current;
      if (!el) return;
      if (isCapsule) {
        el.style.height = `${capsuleMinHeight}px`;
        el.style.overflowY = "hidden";
      } else {
        el.style.height = "36px";
      }
    });
  }, [value, disabled, onSend, attachedImage, isCapsule, capsuleMinHeight]);

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const newVal = e.target.value;
    if (newVal.length <= MAX_CHARS) {
      setValue(newVal);
    }
  };

  const handleFileSelect = useCallback(
    async (e: React.ChangeEvent<HTMLInputElement>) => {
      const file = e.target.files?.[0];
      if (!file) return;

      // Validate file type
      if (!ACCEPTED_IMAGE_TYPES.includes(file.type)) {
        setSnackError(translate("midas.imageUpload.unsupportedFormat"));
        e.target.value = "";
        return;
      }

      // Validate file size
      if (file.size > MAX_IMAGE_SIZE_BYTES) {
        setSnackError(translate("midas.imageUpload.imageTooLarge"));
        e.target.value = "";
        return;
      }

      const resized = await resizeImageForUpload(file);
      setAttachedImage(resized);
      // Reset file input so same file can be re-selected
      e.target.value = "";
    },
    [translate]
  );

  const handleRemoveImage = useCallback(() => {
    setAttachedImage(null);
  }, []);

  const handleAttachClick = useCallback(() => {
    fileInputRef.current?.click();
  }, []);

  // Counter color
  const counterColor =
    charCount >= MAX_CHARS
      ? "#3498DB"
      : charCount >= COUNTER_THRESHOLD
      ? "#F39C12"
      : dt.textMuted;

  const sendBtnSize = isPhone ? 40 : 36;

  // ── Capsule variant ──
  if (isCapsule) {
    return (
      <Box
        sx={{
          backgroundColor: dt.pageBg,
          padding: 0,
        }}
      >
        {/* Hidden file input — no `capture` attribute so the native picker
            shows Camera + Gallery + Files on Android/iOS (BUG-MIDAS-MOBILE-0414). */}
        <input
          ref={fileInputRef}
          type="file"
          accept={ACCEPTED_IMAGE_EXTENSIONS}
          onChange={handleFileSelect}
          style={{ display: "none" }}
        />

        {/* Capsule container */}
        <Box
          sx={{
            margin: isPhone
              ? "0 8px 8px 8px"
              : isTablet
                ? "0 12px 12px 12px"
                : "0 16px 16px 16px",
            borderRadius: isPhone ? "20px" : "24px",
            border: focused
              ? `1.5px solid ${midas.gold}`
              : `1.5px solid ${dt.cardBorder}`,
            boxShadow: focused
              ? `0 2px 12px rgba(0, 0, 0, 0.06), 0 0 0 3px ${midas.goldGlowRing}`
              : "0 2px 12px rgba(0, 0, 0, 0.06)",
            backgroundColor: dt.cardBg,
            padding: isPhone ? "12px 16px 10px 16px" : "16px 20px 12px 20px",
            display: "flex",
            flexDirection: "column",
            transition: "border 0.2s ease, box-shadow 0.2s ease",
            opacity: disabled ? 0.5 : 1,
          }}
        >
          {/* Image preview inside capsule */}
          {attachedImage && imagePreviewUrl && (
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: "8px",
                mb: "8px",
              }}
            >
              <Box
                sx={{
                  position: "relative",
                  width: "56px",
                  height: "56px",
                  borderRadius: "8px",
                  overflow: "hidden",
                  border: `1px solid ${dt.cardBorder}`,
                  flexShrink: 0,
                }}
              >
                <img
                  src={imagePreviewUrl}
                  alt={attachedImage.name}
                  style={{
                    width: "100%",
                    height: "100%",
                    objectFit: "cover",
                    display: "block",
                  }}
                />
                <IconButton
                  size="small"
                  onClick={handleRemoveImage}
                  sx={{
                    position: "absolute",
                    top: "-4px",
                    right: "-4px",
                    width: "20px",
                    height: "20px",
                    backgroundColor: "rgba(0,0,0,0.6)",
                    color: "#fff",
                    "&:hover": { backgroundColor: "rgba(0,0,0,0.8)" },
                  }}
                >
                  <CloseRounded sx={{ fontSize: "12px" }} />
                </IconButton>
              </Box>
              <Box
                sx={{
                  fontFamily: dt.font,
                  fontSize: "12px",
                  color: dt.textMuted,
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                  maxWidth: "200px",
                }}
              >
                {attachedImage.name}
              </Box>
            </Box>
          )}

          {/* Textarea */}
          <textarea
            ref={textareaRef}
            value={value}
            onChange={handleChange}
            onKeyDown={handleKeyDown}
            onFocus={() => setFocused(true)}
            onBlur={() => setFocused(false)}
            disabled={disabled}
            placeholder={translate("placeholder")}
            rows={1}
            style={{
              width: "100%",
              minHeight: `${capsuleMinHeight}px`,
              maxHeight: `${CAPSULE_MAX_HEIGHT}px`,
              backgroundColor: "transparent",
              border: "none",
              padding: 0,
              fontFamily: "var(--font-sans)",
              fontSize: "14px",
              fontWeight: 400,
              color: dt.textPrimary,
              lineHeight: "1.5",
              resize: "none",
              outline: "none",
              overflowY: "hidden",
              boxSizing: "border-box",
              opacity: disabled ? 0.6 : 1,
            }}
          />

          {/* Character counter */}
          {showCounter && (
            <Box
              sx={{
                fontFamily: dt.fontMono,
                fontSize: "10px",
                fontWeight: 500,
                color: counterColor,
                textAlign: "right",
                mt: "2px",
                mr: "4px",
              }}
            >
              {translate("charCount", { count: charCount, max: MAX_CHARS })}
            </Box>
          )}

          {/* Bottom row: attach left, send right */}
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              mt: "4px",
            }}
          >
            {/* Attach / Add button (bottom-left) */}
            <Tooltip
              title={
                imageUploadDisabled
                  ? translate("midas.imageUpload.selectMachineHint")
                  : ""
              }
              placement="top"
              arrow
              disableHoverListener={!imageUploadDisabled}
            >
              <span style={{ display: "inline-flex" }}>
                <IconButton
                  onClick={handleAttachClick}
                  disabled={disabled || imageUploadDisabled}
                  size="small"
                  aria-label={
                    isPhone
                      ? translate("midas.imageUpload.takePhoto")
                      : translate("midas.imageUpload.attachImage")
                  }
                  sx={{
                    width: 32,
                    height: 32,
                    minWidth: 32,
                    color: dt.textMuted,
                    opacity: disabled || imageUploadDisabled ? 0.3 : 0.7,
                    transition: "all 0.15s ease",
                    "&:hover": {
                      color:
                        disabled || imageUploadDisabled ? dt.textMuted : midas.gold,
                      opacity: disabled || imageUploadDisabled ? 0.3 : 1,
                      backgroundColor:
                        disabled || imageUploadDisabled
                          ? "transparent"
                          : midas.goldGlow,
                    },
                  }}
                >
                  {isPhone ? (
                    <CameraAltRounded sx={{ fontSize: "20px" }} />
                  ) : (
                    <AddRounded sx={{ fontSize: "22px" }} />
                  )}
                </IconButton>
              </span>
            </Tooltip>

            {/* Send button (bottom-right) */}
            <Box
              onClick={canSend ? handleSend : undefined}
              sx={{
                width: sendBtnSize,
                height: sendBtnSize,
                minWidth: sendBtnSize,
                borderRadius: "50%",
                background: `linear-gradient(135deg, ${midas.gold} 0%, ${midas.goldDark} 100%)`,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                cursor: canSend ? "pointer" : "default",
                opacity: canSend ? 1 : 0.35,
                boxShadow: canSend
                  ? "0 2px 8px rgba(212, 160, 23, 0.25)"
                  : "none",
                transition: "all 0.15s ease",
                flexShrink: 0,
                "&:hover": canSend
                  ? {
                      transform: "scale(1.05)",
                      boxShadow: "0 4px 12px rgba(212, 160, 23, 0.35)",
                    }
                  : {},
                "&:active": canSend
                  ? {
                      transform: "scale(0.95)",
                    }
                  : {},
              }}
            >
              <SendRounded sx={{ fontSize: "18px", color: "#FFFFFF" }} />
            </Box>
          </Box>
        </Box>

        {/* Error toast */}
        <ActionToast
          open={snackError !== null}
          variant="error"
          message={snackError || ""}
          onClose={() => setSnackError(null)}
          duration={4000}
        />
      </Box>
    );
  }

  // ── Compact (default) variant — original widget style ──
  return (
    <Box
      sx={{
        minHeight: "56px",
        maxHeight: "180px",
        backgroundColor: dt.cardBg,
        borderTop: `1px solid ${dt.cardBorder}`,
        padding: isPhone ? "12px 16px" : "10px 16px",
        display: "flex",
        flexDirection: "column",
        gap: "8px",
      }}
    >
      {/* Image preview row */}
      {attachedImage && imagePreviewUrl && (
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: "8px",
            paddingLeft: "4px",
          }}
        >
          <Box
            sx={{
              position: "relative",
              width: "56px",
              height: "56px",
              borderRadius: "8px",
              overflow: "hidden",
              border: `1px solid ${dt.cardBorder}`,
              flexShrink: 0,
            }}
          >
            <img
              src={imagePreviewUrl}
              alt={attachedImage.name}
              style={{
                width: "100%",
                height: "100%",
                objectFit: "cover",
                display: "block",
              }}
            />
            <IconButton
              size="small"
              onClick={handleRemoveImage}
              sx={{
                position: "absolute",
                top: "-4px",
                right: "-4px",
                width: "20px",
                height: "20px",
                backgroundColor: "rgba(0,0,0,0.6)",
                color: "#fff",
                "&:hover": { backgroundColor: "rgba(0,0,0,0.8)" },
              }}
            >
              <CloseRounded sx={{ fontSize: "12px" }} />
            </IconButton>
          </Box>
          <Box
            sx={{
              fontFamily: dt.font,
              fontSize: "12px",
              color: dt.textMuted,
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
              maxWidth: "200px",
            }}
          >
            {attachedImage.name}
          </Box>
        </Box>
      )}

      {/* Input row */}
      <Box sx={{ display: "flex", alignItems: "flex-end", gap: "8px" }}>
        {/* Hidden file input — no `capture` attribute so the native picker
            shows Camera + Gallery + Files on Android/iOS (BUG-MIDAS-MOBILE-0414). */}
        <input
          ref={fileInputRef}
          type="file"
          accept={ACCEPTED_IMAGE_EXTENSIONS}
          onChange={handleFileSelect}
          style={{ display: "none" }}
        />

        {/* Attach button */}
        <Tooltip
          title={
            imageUploadDisabled
              ? translate("midas.imageUpload.selectMachineHint")
              : ""
          }
          placement="top"
          arrow
          disableHoverListener={!imageUploadDisabled}
        >
          <span style={{ display: "inline-flex", alignSelf: "flex-end" }}>
            <IconButton
              onClick={handleAttachClick}
              disabled={disabled || imageUploadDisabled}
              size="small"
              aria-label={
                isPhone
                  ? translate("midas.imageUpload.takePhoto")
                  : translate("midas.imageUpload.attachImage")
              }
              sx={{
                width: sendBtnSize,
                height: sendBtnSize,
                minWidth: sendBtnSize,
                flexShrink: 0,
                color: dt.textMuted,
                opacity: disabled || imageUploadDisabled ? 0.3 : 0.7,
                transition: "all 0.15s ease",
                "&:hover": {
                  color:
                    disabled || imageUploadDisabled ? dt.textMuted : midas.gold,
                  opacity: disabled || imageUploadDisabled ? 0.3 : 1,
                  backgroundColor:
                    disabled || imageUploadDisabled
                      ? "transparent"
                      : midas.goldGlow,
                },
              }}
            >
              {isPhone ? (
                <CameraAltRounded sx={{ fontSize: "20px" }} />
              ) : (
                <AttachFileRounded sx={{ fontSize: "20px" }} />
              )}
            </IconButton>
          </span>
        </Tooltip>

        {/* Textarea column */}
        <Box sx={{ flex: 1, display: "flex", flexDirection: "column" }}>
          <textarea
            ref={textareaRef}
            value={value}
            onChange={handleChange}
            onKeyDown={handleKeyDown}
            onFocus={() => setFocused(true)}
            onBlur={() => setFocused(false)}
            disabled={disabled}
            placeholder={translate("placeholder")}
            rows={1}
            style={{
              width: "100%",
              minHeight: "36px",
              maxHeight: "100px",
              backgroundColor: dt.inputBg,
              border: focused
                ? `1.5px solid ${midas.gold}`
                : "1.5px solid transparent",
              borderRadius: "12px",
              padding: "8px 14px",
              fontFamily: "var(--font-sans)",
              fontSize: "14px",
              fontWeight: 400,
              color: dt.textPrimary,
              lineHeight: "1.5",
              resize: "none",
              outline: "none",
              overflowY: "auto",
              boxSizing: "border-box",
              opacity: disabled ? 0.6 : 1,
              boxShadow: focused ? `0 0 0 3px ${midas.goldGlow}` : "none",
              transition: "border 0.15s ease, box-shadow 0.15s ease",
            }}
          />

          {/* Character counter */}
          {showCounter && (
            <Box
              sx={{
                fontFamily: dt.fontMono,
                fontSize: "10px",
                fontWeight: 500,
                color: counterColor,
                textAlign: "right",
                mt: "2px",
                mr: "4px",
              }}
            >
              {translate("charCount", { count: charCount, max: MAX_CHARS })}
            </Box>
          )}
        </Box>

        {/* Send button */}
        <Box
          onClick={canSend ? handleSend : undefined}
          sx={{
            width: sendBtnSize,
            height: sendBtnSize,
            minWidth: sendBtnSize,
            borderRadius: "50%",
            background: `linear-gradient(135deg, ${midas.gold} 0%, ${midas.goldDark} 100%)`,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            cursor: canSend ? "pointer" : "default",
            opacity: canSend ? 1 : 0.4,
            boxShadow: canSend
              ? "0 2px 8px rgba(212, 160, 23, 0.25)"
              : "none",
            transition: "all 0.15s ease",
            flexShrink: 0,
            alignSelf: "flex-end",
            "&:hover": canSend
              ? {
                  transform: "scale(1.05)",
                  boxShadow: "0 4px 12px rgba(212, 160, 23, 0.35)",
                }
              : {},
            "&:active": canSend
              ? {
                  transform: "scale(0.95)",
                }
              : {},
          }}
        >
          <SendRounded sx={{ fontSize: "18px", color: "#FFFFFF" }} />
        </Box>
      </Box>

      {/* Error toast */}
      <ActionToast
        open={snackError !== null}
        variant="error"
        message={snackError || ""}
        onClose={() => setSnackError(null)}
        duration={4000}
      />
    </Box>
  );
};

export default ChatInputArea;
